import {
  type ClipboardEvent,
  type FocusEvent,
  type FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  Combobox,
  type ComboboxProps,
  type ComboboxState,
} from 'ariakit/combobox'
import {
  Button,
  Center,
  csx,
  IconMagnifyingGlass,
  IconX,
  Spinner,
  Tooltip,
  VisuallyHidden,
} from '@vtex/admin-ui'
import { useAnalytics } from '@/contexts/analytics'
import type { ClickOnSearchFieldEvent } from '@/contexts/analytics/customEvents'
import { useShortcut } from '@/contexts/shortcut'

import type { ProductSearchByOptions } from '../../hooks'

import * as styles from './ComboboxSearch.styles'

type ComboboxSearchProps = Omit<
  ComboboxProps,
  'onSubmit' | 'onFocus' | 'onPaste'
> & {
  state: ComboboxState
  onClear: () => void
  loading?: boolean
  onSubmit: (value: string) => void
  searchingBy?: ProductSearchByOptions
}

export function ComboboxSearch({
  state,
  disabled = false,
  loading = false,
  onClear = () => undefined,
  onBlur = () => undefined,
  placeholder,
  onSubmit,
  searchingBy,
  ...props
}: ComboboxSearchProps) {
  const analytics = useAnalytics()
  const { formatMessage } = useIntl()
  const searchFieldRef = useRef<HTMLInputElement>(null)
  const isFirstRender = useRef(true)

  const hasValue = state.value.toString().length > 0
  const hasClearButton = hasValue && !disabled
  const [shouldShowPlaceholder, setShouldShowPlaceholder] = useState(false)

  const dispatchSearchFieldEvents = useCallback(
    (action: ClickOnSearchFieldEvent['Action']) =>
      analytics.track('Click on search field', {
        Action: action,
      }),
    [analytics]
  )

  const handleFocusShortcut = useCallback(() => {
    searchFieldRef.current?.focus()
    state.show()
    dispatchSearchFieldEvents('Shortcut')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClickOnSearchField = () => {
    dispatchSearchFieldEvents('Click')
    setShouldShowPlaceholder(false)
    searchFieldRef.current?.focus()
  }

  const handleOnSearchFocus = () => {
    setShouldShowPlaceholder(false)
    unsubscribe()
  }

  const handleOnSearchFocusOut = (evt: FocusEvent<HTMLInputElement>) => {
    setShouldShowPlaceholder(!hasValue)
    subscribe()
    onBlur(evt)
  }

  const handleOnSubmit = (evt: FormEvent) => {
    evt.stopPropagation()
    evt.preventDefault()
    onSubmit(state.value)
  }

  const icon = loading ? (
    <Spinner size={16} className={csx(styles.icon(disabled))} />
  ) : (
    <IconMagnifyingGlass size="small" className={csx(styles.icon(disabled))} />
  )

  const { subscribe, unsubscribe } = useShortcut('/', handleFocusShortcut)

  useEffect(() => {
    if (
      searchFieldRef.current &&
      !disabled &&
      !loading &&
      isFirstRender.current
    ) {
      searchFieldRef.current.focus()
      isFirstRender.current = false
      dispatchSearchFieldEvents('Automatic focus')
      setShouldShowPlaceholder(false)
    }
  }, [disabled, dispatchSearchFieldEvents, loading])

  const removeWhiteSpacesOnPaste = (evt: ClipboardEvent<HTMLInputElement>) => {
    evt.preventDefault()
    const clipboardData = evt.clipboardData?.getData('text/plain')

    if (clipboardData) {
      state.setValue(clipboardData.trim())
      state.show()
    }
  }

  return (
    <form
      role="search"
      className={csx({
        ...styles.form,
        ...(disabled ? styles.formDisabled : {}),
      })}
      onSubmit={handleOnSubmit}
      onClick={handleClickOnSearchField}
    >
      <Center className={csx(styles.innerContainer)}>{icon}</Center>
      <VisuallyHidden>
        <label htmlFor="search-combobox">{placeholder}</label>
      </VisuallyHidden>
      {searchingBy && (
        <span key={searchingBy} className={csx(styles.searchingBy)}>
          {formatMessage({
            id: `admin/admin.catalog.product-searching-by-${searchingBy}`,
            defaultMessage: getDefaultMessageForSearching(searchingBy),
          })}
        </span>
      )}
      {shouldShowPlaceholder && !searchingBy && (
        <span className={csx(styles.placeholder)}>
          <FormattedMessage
            id="admin/admin.catalog.products-search-placeholder"
            defaultMessage="Use / start search"
            values={{
              kbd: (text) => (
                <span className={csx(styles.shortcut)}>{text}</span>
              ),
            }}
          />
        </span>
      )}
      <Combobox
        ref={searchFieldRef}
        id="search-combobox"
        state={state}
        className={`${styles.input} amp-unmask`}
        aria-placeholder={formatMessage({
          id: 'admin/admin.catalog.products-search-placeholder',
          defaultMessage: 'Use / to search',
        })}
        onFocus={handleOnSearchFocus}
        onBlur={handleOnSearchFocusOut}
        onPaste={removeWhiteSpacesOnPaste}
        {...props}
      />
      {hasClearButton && (
        <Tooltip
          text={formatMessage({
            id: 'admin/admin.catalog.products-search.clear-field',
            defaultMessage: 'Clear',
          })}
          placement="top"
        >
          <Button
            icon={<IconX />}
            variant="neutralTertiary"
            className={csx(styles.clearButton)}
            onClick={onClear}
            aria-label={formatMessage({
              id: 'admin/admin.catalog.products-search.clear-field',
              defaultMessage: 'Clear search field',
            })}
          />
        </Tooltip>
      )}
      <VisuallyHidden>
        <button type="submit" tabIndex={-1}>
          {placeholder}
        </button>
      </VisuallyHidden>
    </form>
  )
}

function getDefaultMessageForSearching(searchingBy?: ProductSearchByOptions) {
  switch (searchingBy) {
    case 'ean':
      return 'SKU EAN'
    case 'productName':
      return 'Product name'
    case 'skuName':
      return 'SKU name'
    case 'productRefId':
      return 'Product reference code'
    case 'skuRefId':
      return 'SKU reference code'
    case 'productId':
      return 'Product ID'
    case 'skuId':
      return 'SKU ID'
    default:
      return ''
  }
}
